import React, { useId } from 'react';

import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { useMaskInput } from 'use-mask-input';

import { AdministrativeTranslation } from 'src/features/administration/utils/types';
import { useGetCapacityStatistic } from 'src/features/statistic/api/useGetCapacityStatistic';
import BarChart from 'src/features/statistic/components/chart/BarChart';
import HalfPieChart from 'src/features/statistic/components/chart/HalfPieChart';
import MapLegend from 'src/features/statistic/components/MapLegend';
import {
    CAPACITY_STATS_COUNT_KEYS,
    CAPACITY_TOTALS_KEYS,
    CapacityAverageDuration,
    DataKey,
    TOTAL_VISITS_KEY,
    TransformedBarItem,
    VisitStatisticHourlyBreakdown,
} from 'src/features/statistic/types';
import {
    allColorCategoryMapping,
    capacityHourlyBreakdownColorMapping,
    colorCategoryMapping,
} from 'src/features/statistic/utils/constants';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { useListZones } from 'src/features/zone/api/useListZones';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import StatisticTable from 'src/registry/components/statistic/StatisticTable';
import { tableMessages } from 'src/registry/components/table';
import { Button } from 'src/registry/ui/Button';
import DatePicker from 'src/registry/ui/DatePicker';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { Input } from 'src/registry/ui/Input';
import { ICON_MAP } from 'src/utils/constants';
import { formatDurationAsHours } from 'src/utils/formatDuration';
import { safeFormatMessage } from 'src/utils/intl';

type FormattedData = {
    totalVisitsData: {
        title: string;
        value: number;
    }[];
    visitsUntilTimeData: {
        title: string;
        value: number;
    }[];
    capacityData: {
        title: string;
        value: number;
    }[];
};

type SelectedZone = {
    name: string;
    id: string;
};

type WidgetCapacityProps = {
    date: dayjs.Dayjs;
    // Both widgets on the screen load the same endpoint, so the toast is raised once by the route
    // instead of once per widget.
    onLoadErrorChange?: (hasError: boolean) => void;
};

export default function WidgetCapacity({ date, onLoadErrorChange }: WidgetCapacityProps) {
    const { formatMessage, locale } = useIntl();
    const [zone, setZone] = React.useState<SelectedZone | null>(null);
    const id = useId();

    const datetimeMask = useMaskInput({
        mask: 'datetime',
        options: {
            placeholder: '0',
            inputFormat: 'HH:mm',
            outputFormat: 'HH:mm',
            showMaskOnHover: false,
        },
    });

    const columns: ColumnDef<CapacityAverageDuration>[] = [
        {
            accessorKey: 'visitor_type',
            header: formatMessage(statisticMessages.visitorTypeColumn),
            cell: ({ row }) => {
                return (
                    <div className="flex flex-row items-center gap-1">
                        <div
                            style={{
                                backgroundColor: allColorCategoryMapping[row.original.visitor_type],
                                width: 10,
                                height: 10,
                                borderRadius: 50,
                            }}
                        />
                        <div>{safeFormatMessage(formatMessage, row.original.visitor_type)}</div>
                    </div>
                );
            },
        },
        {
            accessorKey: 'total_visits_count',
            header: formatMessage(statisticMessages.visitsCount),
        },
        {
            accessorKey: 'avg_duration_seconds',
            header: formatMessage(statisticMessages.averageDuration),
            cell: ({ row }) => formatDurationAsHours(row.original.avg_duration_seconds),
        },
    ];

    const translate = React.useCallback(
        (translations?: AdministrativeTranslation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    const [dateTime, setDateTime] = React.useState({
        date,
        time: date.format('HH:mm'),
    });

    const { parkingId } = useParking();

    const { data, isLoading, isError } = useGetCapacityStatistic({
        from_datetime: dateTime.date.format('YYYY-MM-DD'),
        time: dateTime.time,
        zone: zone?.id ?? null,
        parkingId,
    });
    const { data: zoneData } = useListZones({ parkingId });

    React.useEffect(() => {
        onLoadErrorChange?.(isError);
    }, [isError, onLoadErrorChange]);

    const { totalVisitsData, capacityData, visitsUntilTimeData }: FormattedData = React.useMemo(() => {
        if (!data)
            return {
                totalVisitsData: [],
                visitsUntilTimeData: [],
                capacityData: [],
            };
        const totalVisitsData = Object.entries(data.pie_charts_section_stats.total_visits_pie_chart)
            .filter(([key, value]) => value > 0 && key !== TOTAL_VISITS_KEY)
            .map(([key, value]) => ({
                title: key,
                value,
            }));

        const visitsUntilTimeData = Object.entries(data.pie_charts_section_stats.visits_until_time_pie_chart)
            .filter(([key, value]) => value > 0 && key !== TOTAL_VISITS_KEY)
            .map(([key, value]) => ({
                title: key,
                value,
            }));

        const capacityData = Object.entries(data.pie_charts_section_stats.capacity_at_time_pie_chart)
            .filter(([key, value]) => value > 0 && !CAPACITY_TOTALS_KEYS.includes(key))
            .map(([key, value]) => ({
                title: key,
                value,
            }));

        return {
            capacityData,
            totalVisitsData,
            visitsUntilTimeData,
        };
    }, [data]);

    const legendColorMapping: Record<string, string> = React.useMemo(() => {
        // A category stays in the legend only while one of the charts above actually draws a slice
        // for it. Every array is already filtered to the values above zero, which keeps the legend
        // from ever disagreeing with the slices
        const categoriesWithVisits: Set<string> = new Set(
            [...totalVisitsData, ...visitsUntilTimeData].map(({ title }) => title),
        );

        const legendEntries: [string, string][] = Object.entries(colorCategoryMapping).filter(([category]) =>
            categoriesWithVisits.has(category),
        );

        // The capacity chart draws the free common spaces in a colour of their own, so the legend
        // carries them too - they belong to no visitor category
        const hasFreeCommonSpaces: boolean = capacityData.some(
            ({ title }) => title === CAPACITY_STATS_COUNT_KEYS.FREE_COMMON_SPACES_COUNT,
        );

        if (hasFreeCommonSpaces) {
            legendEntries.push([
                CAPACITY_STATS_COUNT_KEYS.FREE_COMMON_SPACES_COUNT,
                allColorCategoryMapping[CAPACITY_STATS_COUNT_KEYS.FREE_COMMON_SPACES_COUNT],
            ]);
        }

        return Object.fromEntries(legendEntries);
    }, [totalVisitsData, visitsUntilTimeData, capacityData]);

    const transformPaymentData = React.useCallback(
        ({
            data,
        }: {
            data?: VisitStatisticHourlyBreakdown;
        }): {
            dataKeys: DataKey[];
            data: TransformedBarItem[];
        } => {
            if (!data)
                return {
                    dataKeys: [],
                    data: [],
                };

            const dataKeys: DataKey[] = Array.from(
                Object.values(data).reduce((acc, currentValue) => {
                    Object.keys(currentValue).map((key) => acc.add(key));

                    return acc;
                }, new Set<string>()),
            ).map((key) => ({
                title: safeFormatMessage(formatMessage, `${key}s`, key),
                value: key,
            }));

            const newData: TransformedBarItem[] = Object.entries(data).map(([key, item]) => ({
                ...item,
                title: safeFormatMessage(formatMessage, key.replaceAll('_', '-')),
            }));

            return {
                dataKeys,
                data: newData,
            };
        },
        [formatMessage],
    );

    return (
        <GeneralBlock className="flex-1 gap-6">
            <div className="flex min-h-60 flex-1 flex-col">
                <div className="z-20 flex flex-wrap justify-between">
                    <div className="flex flex-row gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" className="text-sm">
                                    <span className="max-w-30 truncate">
                                        {zone?.name ?? formatMessage(statisticMessages.allZones)}
                                    </span>
                                    <FontAwesomeIcon icon={ICON_MAP.CHEVRON_DOWN} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                                <DropdownMenuLabel>{formatMessage(statisticMessages.chooseZone)}</DropdownMenuLabel>
                                {(zoneData?.results?.length || 0) > 0 && <DropdownMenuSeparator />}
                                {zoneData?.results.map((z) => (
                                    <DropdownMenuCheckboxItem
                                        key={z.id}
                                        checked={z.id === zone?.id}
                                        onSelect={() => {
                                            setZone({
                                                id: z.id,
                                                name: translate(z.translations),
                                            });
                                        }}
                                    >
                                        {translate(z.translations)}
                                    </DropdownMenuCheckboxItem>
                                ))}
                                <DropdownMenuSeparator />
                                <DropdownMenuItemWithIcon
                                    variant="destructive"
                                    icon={ICON_MAP.DELETE}
                                    onSelect={() => setZone(null)}
                                >
                                    {formatMessage(tableMessages.clearFilter)}
                                </DropdownMenuItemWithIcon>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* There are no statistics for a day that has not happened yet */}
                        <DatePicker
                            value={new Date(dateTime.date.toISOString())}
                            buttonValue={dateTime.date.format('DD.MM.YYYY')}
                            onChange={(date) => setDateTime({ ...dateTime, date: dayjs(date) })}
                            maxDate={dayjs().endOf('day').toDate()}
                        />
                        <div className="relative flex w-24 items-center">
                            <FontAwesomeIcon
                                icon={faClock}
                                size="sm"
                                className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
                            />
                            <Input
                                id={id}
                                type="text"
                                placeholder="00-23:59"
                                value={dateTime.time}
                                onChange={({ target }) => setDateTime({ ...dateTime, time: target.value })}
                                ref={datetimeMask}
                                className="pl-10 text-sm"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex min-h-48 w-full gap-2">
                    <HalfPieChart
                        data={totalVisitsData}
                        dataKey="value"
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.totalVisits)}
                        value={data?.pie_charts_section_stats.total_visits_pie_chart.total_visits_count}
                    />
                    <HalfPieChart
                        data={visitsUntilTimeData}
                        dataKey="value"
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.occupiedSpacesAt, { time: dateTime.time })}
                        value={data?.pie_charts_section_stats.visits_until_time_pie_chart.total_visits_count}
                    />
                    <HalfPieChart
                        data={capacityData}
                        dataKey="value"
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.freeSpacesAt, { time: dateTime.time })}
                        value={data?.pie_charts_section_stats.capacity_at_time_pie_chart.total_free_spaces_count}
                    />
                </div>

                <MapLegend colorMapping={legendColorMapping} />
            </div>
            <div className="grid grid-cols-1 gap-2">
                <StatisticTable
                    className="h-fit flex-1"
                    title={formatMessage(statisticMessages.totalVisitsBreakdownDay)}
                    data={data?.visits_breakdown_for_day_stats ?? []}
                    columns={columns as any}
                />
                <StatisticTable
                    className="h-fit flex-1"
                    title={formatMessage(statisticMessages.totalVisitsBreakdownTime, { time: dateTime.time })}
                    data={data?.visits_breakdown_for_time_stats ?? []}
                    columns={columns as any}
                />
            </div>
            <div className="flex flex-col gap-2">
                <SectionBlockTitle>{formatMessage(statisticMessages.capacityHourlyBreakdownDay)}</SectionBlockTitle>
                <BarChart
                    isLoading={isLoading}
                    colorMapping={capacityHourlyBreakdownColorMapping}
                    emptyTitle={formatMessage(statisticMessages.noVisitsFound)}
                    {...transformPaymentData({ data: data?.capacity_hourly_breakdown_for_day_stats })}
                />
            </div>
        </GeneralBlock>
    );
}
